import { smIconSize } from '@/constants';
import { useAuthStatus } from '@/hooks';
import { useRoles } from '@/hooks/useRoles';
import { useAppStore } from '@/stores/app';
import { Org } from '@/types';
import Profile from '@components/Profile';
import Settings from '@components/Settings';
import { Avatar, AvatarProps } from '@nautilusbio/nb-design-system/Avatar';
import { Button } from '@nautilusbio/nb-design-system/Button';
import type { ButtonProps } from '@nautilusbio/nb-design-system/Button';
import Divider from '@nautilusbio/nb-design-system/Divider';
import { Grid } from '@nautilusbio/nb-design-system/Grid';
import { GridItem } from '@nautilusbio/nb-design-system/GridItem';
import { Popover } from '@nautilusbio/nb-design-system/Popover';
import { getStackClassName, Stack } from '@nautilusbio/nb-design-system/Stack';
import type { StackProps } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import clsx from 'clsx';
import { ArrowLeftToLine, ArrowRightToLine, KeyRound, LogOut } from 'lucide-react';
import React, { FC, ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import styles from '../styles.module.css';

import DevControls from './DevControls';
import OrgSelector from './OrgSelector';
import RoleSwitcher from './RoleSwitcher';

const defaultButtonProps: Omit<ButtonProps, 'data-testid'> = {
  fullWidth: true,
  size: 'sm',
};

const UserMenu: FC<{
  authLink?: ReactNode;
  helpButton: ReactNode;
  selectedOrg?: Org;
  onLogout: () => void;
  isSidebarCollapsed: boolean;
  isLoggingOut?: boolean;
  toggleHandler: () => void;
}> = ({ authLink, helpButton, selectedOrg, onLogout, isSidebarCollapsed, isLoggingOut = false, toggleHandler }) => {
  const { t } = useTranslation('sidebar');
  const { user } = useAuthStatus();
  const { isGlobalSuperAdmin } = useRoles();

  const { isSidebarMenuOpen, setIsSidebarMenuOpen, setIsOrgSelectorOpen } = useAppStore(
    useShallow(state => ({
      isSidebarMenuOpen: state.isSidebarMenuOpen,
      setIsSidebarMenuOpen: state.setIsSidebarMenuOpen,
      setIsOrgSelectorOpen: state.setIsOrgSelectorOpen,
    })),
  );
  // Select icon component directly
  const ToggleIcon = isSidebarCollapsed ? ArrowRightToLine : ArrowLeftToLine;

  const openOrgSelector = useCallback(() => {
    // Collapsed, the org selector renders inside this popover, so closing it would unmount it.
    if (!isSidebarCollapsed) setIsSidebarMenuOpen(false);
    setIsOrgSelectorOpen(true);
  }, [isSidebarCollapsed, setIsSidebarMenuOpen, setIsOrgSelectorOpen]);

  const footerControls = useMemo(
    () => (
      <Stack className={styles.userMenuControlsFullSidebar} fullHeight fullWidth gap={2}>
        {selectedOrg && isSidebarCollapsed && (
          <>
            {helpButton}
            <OrgSelector selectedOrg={selectedOrg} />
            <RoleSwitcher isSidebarCollapsed={isSidebarCollapsed} selectedOrg={selectedOrg} />
            {authLink}
          </>
        )}

        {isSidebarCollapsed && <Divider className={styles.helpOrgDivider} />}

        <Grid columns={2} fullWidth gap={2}>
          {user && <Profile id="profile" />}

          <Settings id={'settings'} title={t('settings', { ns: 'general' })} />

          {/* Cross-org role assumption is what the org selector already does for a super admin. */}
          {isGlobalSuperAdmin() && selectedOrg && (
            <GridItem columnSpan={2}>
              <Button {...defaultButtonProps} data-testid="assume-role-button" onPress={openOrgSelector}>
                <KeyRound className={styles.logoutIcon} size={16} />
                {t('assume_role')}
              </Button>
            </GridItem>
          )}

          {user && (
            <GridItem columnSpan={2}>
              <Button
                {...defaultButtonProps}
                aria-label={t('buttons.logout', { ns: 'login' })}
                data-testid={isSidebarCollapsed ? 'logout-button-collapsed' : 'logout-button-expanded'}
                isDisabled={isLoggingOut}
                onPress={onLogout}
                visual="danger"
              >
                <LogOut className={styles.logoutIcon} size={16} />
                {isLoggingOut ? t('logging_out', { ns: 'login' }) : t('buttons.logout', { ns: 'login' })}
              </Button>
            </GridItem>
          )}
        </Grid>

        <Text as="span" className={styles.appVersion}>
          v{__APP_VERSION__}
        </Text>
      </Stack>
    ),
    [
      authLink,
      helpButton,
      isSidebarCollapsed,
      selectedOrg,
      user,
      onLogout,
      t,
      isLoggingOut,
      isGlobalSuperAdmin,
      openOrgSelector,
    ],
  );

  const footerContent: Partial<StackProps> & { children: ReactNode } = useMemo(
    () => ({
      children: (
        <>
          <DevControls />
          {footerControls}
        </>
      ),
    }),
    [footerControls],
  );

  const avatarProps: AvatarProps = useMemo(
    () => ({
      className: styles.sidebarAvatar,
      children: user?.full_name,
      hasTooltip: true,
      size: 'xs3',
    }),
    [user?.full_name],
  );

  const optionsToggleHandler = () => {
    setIsSidebarMenuOpen(!isSidebarMenuOpen);
    setIsOrgSelectorOpen(false);
  };

  return (
    <Popover
      isOpen={isSidebarMenuOpen}
      padding={0}
      content={
        <Stack
          {...footerContent}
          className={clsx(styles.popoverContent, styles.userMenuControls)}
          fullHeight
          fullWidth
          orientation="vertical"
        />
      }
      onClickOutside={() => setIsSidebarMenuOpen(false)}
      data-testid="sidebar-user-menu-popover"
      positions={['top', 'right']}
      align="start"
      showArrow
    >
      <Stack alignItems="center" fullWidth justifyContent="space-between" orientation="horizontal">
        <Tooltip
          key={isSidebarMenuOpen ? 'menu-open' : 'menu-closed'}
          content={t('user_menu')}
          label={t('user_menu')}
          hoverOptions={{ delay: { open: 500, close: 0 } }}
          isDisabled={isSidebarMenuOpen}
          offset={8}
          placement="top"
          size="sm"
          trigger={
            <Button
              className={getStackClassName(['displayflex', 'horizontal', 'gap0'])}
              data-testid="sidebar-avatar-button"
              fullWidth
              onPress={optionsToggleHandler}
              visual="unstyled"
            >
              <Avatar {...avatarProps} />

              {!isSidebarCollapsed && (
                <Text as="span" size="xs">
                  {user?.full_name}
                </Text>
              )}
            </Button>
          }
        />

        <Tooltip
          content={isSidebarCollapsed ? t('toggle.expand') : t('toggle.collapse')}
          label={isSidebarCollapsed ? t('toggle.expand') : t('toggle.collapse')}
          hoverOptions={{ delay: { open: 1000, close: 0 } }}
          offset={8}
          placement="right"
          size="sm"
          trigger={
            <Button
              aria-label={isSidebarCollapsed ? t('toggle.expand') : t('toggle.collapse')}
              data-testid={isSidebarCollapsed ? 'sidebar-toggle-expand-btn' : 'sidebar-toggle-collapse-btn'}
              onPress={toggleHandler}
              size="xs"
              visual="unstyled"
            >
              <ToggleIcon size={smIconSize} />
            </Button>
          }
        />
      </Stack>
    </Popover>
  );
};

export default UserMenu;
